import { Icon } from '../../../components/index.js';
import { formatLongDate } from '../../../lib/format-date.js';
import { PLATE_TITLE } from '../../../styles/plate-type.js';
import { SelectedProfileTitle } from './SelectedProfileTitle.js';

/**
 * The identity block of the profile hero: username (the page's `<h1>`), the
 * join date, and — when one is selected — the player's profile title
 * (APP_SPEC §13, §13.2). Username casing is whatever the server returned
 * (APP_SPEC §9).
 *
 * The join date is written out in full beside a calendar glyph rather than
 * shown as a bare numeric date: this is a decorative profile plate, not a
 * data table, and `9/7/2026` is also ambiguous across locales. The title
 * below is separated by its own small ruled ornament, so the block reads as
 * name → when → accolade rather than three stacked lines.
 */
export interface ProfileIdentityProps {
  username: string;
  joinedAt: string;
  titleId: string | null;
}

export function ProfileIdentity({ username, joinedAt, titleId }: ProfileIdentityProps) {
  return (
    <div className="min-w-0 shrink-0">
      {/* Not `.page-title`: that is the app's Cinzel/Palatino display face at
          a page heading's size, and on the plate the name has to hold its own
          beside a 176px portrait. Set instead in the plate's own Alegreya at
          bold, so the whole hero — name, joined date, accolade, Overall
          heading, labels and numerals — is one loaded family (CLAUDE.md §9's
          single-source principle applied to type: the face comes from
          `fontFamily.profile`, not from a local stack). */}
      <h1 className={`${PLATE_TITLE} text-textPrimary`}>{username}</h1>
      {/* At enough weight and ink to stay legible over whatever theme artwork
          sits behind it — the old `text-sm text-textSecondary` line went thin
          and brown against a lit background. Still plainly subordinate to the
          username above. */}
      <p className="mt-2 flex items-center gap-2 font-profile text-[1rem] font-medium leading-snug text-textPrimary/85">
        <Icon name="calendar" className="h-[1.05rem] w-[1.05rem] shrink-0 text-accent" />
        <span className="[font-variant-numeric:lining-nums]">
          Joined {formatLongDate(joinedAt)}
        </span>
      </p>
      <SelectedProfileTitle titleId={titleId} />
    </div>
  );
}
